<!--
// app/pages/[region]/stats/best-goalkeeper.vue
-->
<script setup lang="ts">
import type { RankingsData } from "~~/src/player/domain/Player";
import type { GroupedRanking } from "~~/src/stats/domain/models/Rankings";
import { useSeasonFilter } from "~/composables/season/seasonFilter";
import { useSeasons } from "~/composables/season/seasons";
import statsService from "~~/src/stats/domain/services/statsService";
import SeasonsFilter from "~/components/Shared/SeasonsFilter.vue";
import HeaderBannerTitle from "~/components/Shared/HeaderBannerTitle.vue";
import SidebarMenuStats from "~/components/pages/region-stats/SidebarMenuStats.vue";
import BaseBackdropLoading from "~/components/base/BaseBackdropLoading.vue";
import { setAliasUrl } from "~~/src/Shared/utils";
import PrimeDataTable from "primevue/datatable";
import PrimeColumn from "primevue/column";
import { $SEASONS_WITHOUT_DATA } from "~~/src/season-cluster/infrastructure/ui-services/seasonUiService";
import { useNuxtApp } from "#imports";

const { $t, $localeRoute } = useNuxtApp();

definePageMeta({
  middleware: [
    "not-found-americas",
    "not-found-world-cup",
    "not-found-italy",
    "not-found-queens-americas",
    "not-found-nations",
    "not-found-king-of-kings-nations",
    "not-found-brazil",
    "not-found-france",
    "not-found-germany",
    "not-found-mena",
  ],
});

const { getSeasonParameterStats } = useSeasons();
const { seasonId, seasonClusterId } = useSeasonFilter();
const { mergeRankingsByPlayerId } = statsService();
const router = useRouter();
const tableStatsKey = ref<number>(0);
const sidebarKey = ref<number>(0);
const loadingSeasonData = ref<boolean>(false);
const seasonGoalkeepersRatio = ref<RankingsData[]>();
const seasonGoalkeepers = ref<RankingsData[]>();
const matchesPlayed = ref<RankingsData[]>();
const tableData = ref<GroupedRanking[]>();
const errorMessage = ref<boolean>(false);

const getSeasonData = async (): Promise<void> => {
  if (!!seasonId.value && $SEASONS_WITHOUT_DATA.includes(Number(seasonId.value))) {
    try {
      loadingSeasonData.value = true;
      errorMessage.value = false;

      const seasonIdValue = seasonId.value;

      seasonGoalkeepersRatio.value = seasonIdValue
        ? await getSeasonParameterStats(seasonIdValue, "GOL-ALLS")
        : undefined;
      seasonGoalkeepers.value = seasonIdValue
        ? await getSeasonParameterStats(seasonIdValue, "GOL-ALLS")
        : undefined;
      matchesPlayed.value = seasonIdValue ? await getSeasonParameterStats(seasonIdValue, "PG") : undefined;

      tableData.value =
        seasonGoalkeepers.value &&
        matchesPlayed.value &&
        seasonGoalkeepersRatio.value &&
        mergeRankingsByPlayerId(seasonGoalkeepersRatio.value, [
          seasonGoalkeepers.value,
          matchesPlayed.value,
        ]).filter(rank => rank.player.role === "goalkeeper");
    } catch (e) {
      errorMessage.value = true;
    } finally {
      loadingSeasonData.value = false;
      tableStatsKey.value++;
      sidebarKey.value++;
    }
  } else {
    await nextTick();
    await router.push(
      $localeRoute({ name: "region-stats", query: { season: seasonClusterId.value, split: seasonId.value } })
    );
  }
};

await nextTick();
await getSeasonData();

useSeoMeta({
  title:
    $t("menuItem.stats") +
    " " +
    $t("menuItem.statsPlayers") +
    " - " +
    $t("stats.sidebar.menu.best-goalkeeper"),
  ogTitle:
    $t("menuItem.stats") +
    " " +
    $t("menuItem.statsPlayers") +
    " - " +
    $t("stats.sidebar.menu.best-goalkeeper"),
});
</script>

<template>
  <header-banner-title
    :title="$t('general.stats.title') + ' ' + $t('menuItem.statsPlayers')"
  ></header-banner-title>
  <section class="px-4 md:px-6">
    <div class="kql-w-container">
      <seasons-filter
        v-model:seasonClusterId="seasonClusterId"
        v-model:seasonId="seasonId"
        @change="getSeasonData"
      />
    </div>
  </section>

  <section class="px-4 md:px-6 my-9">
    <div class="kql-w-container grid grid-cols-1 lg:grid-cols-4 py-9 px-4 bg-gray-900 rounded-lg lg:gap-4">
      <div class="mb-9 lg:mb-0 relative">
        <sidebar-menu-stats
          :is-blocked="seasonId === 3"
          :new-stats="!$SEASONS_WITHOUT_DATA.includes(Number(seasonId))"
          :key="sidebarKey"
        ></sidebar-menu-stats>
      </div>
      <div class="col-span-3 relative">
        <base-backdrop-loading :active="loadingSeasonData"></base-backdrop-loading>

        <div v-if="errorMessage || (tableData && !tableData.length)">
          {{ $t("stats.message.notAvailable") }}
        </div>

        <div class="stats-table-data max-w-full overflow-hidden" v-if="tableData && tableData.length">
          <PrimeDataTable :value="tableData" scrollable scrollHeight="540px">
            <PrimeColumn
              field="rankingData"
              header="Ranking"
              frozen
              class="ranking max-w-[120px] min-w-[75px]"
              style="max-width: 120px; min-width: 75px"
              ><template #body="slotProps"
                ><span v-if="slotProps && slotProps.data">{{
                  slotProps.data?.rankingData[0]?.ranking ? slotProps.data?.rankingData[0]?.ranking : 0
                }}</span></template
              >
            </PrimeColumn>
            <PrimeColumn
              field="player.shortName"
              :header="$t('cards.stats.name.text')"
              class="player-name max-w-[200px] min-w-[130px] leading-tight"
              style="max-width: 200px; min-width: 130px"
              frozen
            ></PrimeColumn>
            <PrimeColumn
              field="team"
              :header="$t('tables.team.word')"
              class="team pe-2 md:pe-0 max-w-[80px] min-w-[50px]"
              style="max-width: 80px; min-width: 50px"
            >
              <template #body="slotProps">
                <NuxtImg
                  v-if="slotProps && slotProps.data?.team"
                  loading="lazy"
                  :src="setAliasUrl(slotProps.data.team.logo.url)"
                  :alt="slotProps.data.team.completeName"
                  class="team-logo"
                  width="48"
                  height="48"
                  sizes="40px sm:48px"
                />
              </template>
            </PrimeColumn>
            <PrimeColumn field="0" style="max-width: 600px; min-width: 40px; width: 100%"></PrimeColumn>
            <PrimeColumn
              field="rankingData"
              :header="$t('tables.gamesPlayed.ab')"
              class="text-center data-column max-w-[60px] min-w-[45px]"
              style="min-width: 45px; max-width: 60px"
            >
              <template #body="slotProps"
                ><span v-if="slotProps && slotProps.data">{{
                  slotProps.data?.rankingData[2]?.total ? slotProps.data?.rankingData[2]?.total : 0
                }}</span></template
              >
            </PrimeColumn>
            <PrimeColumn
              field="rankingData"
              :header="$t('G.En')"
              class="text-center data-column max-w-[60px] min-w-[45px] pr-4 initial-col-highlight"
              style="min-width: 45px; max-width: 60px"
              ><template #body="slotProps"
                ><span v-if="slotProps && slotProps.data">{{
                  slotProps.data?.rankingData[1]?.total ? slotProps.data?.rankingData[1]?.total : 0
                }}</span></template
              ></PrimeColumn
            >
            <PrimeColumn
              field="rankingData"
              header="Ratio"
              class="text-center data-column max-w-[60px] min-w-[45px] pe-4"
              style="min-width: 45px; max-width: 60px"
              ><template #body="slotProps"
                ><span v-if="slotProps && slotProps.data">{{
                  slotProps.data?.rankingData[0]?.totalAVG
                    ? slotProps.data?.rankingData[0]?.totalAVG.toFixed(2)
                    : 0
                }}</span></template
              ></PrimeColumn
            >
          </PrimeDataTable>
        </div>
        <!--          <div v-else-if="tableData" class="max-w-full overflow-x-scroll">-->
        <!--            <table-data-stats-->
        <!--              :key="tableStatsKey"-->
        <!--              :table-data="tableData"-->
        <!--              :table-header="assistsTableHeader"-->
        <!--              :loading="loadingSeasonData"-->
        <!--              main-ranking-parameter-code="ASS-V"-->
        <!--              :values-shown-for-rankings-data="['total', 'total']"-->
        <!--            ></table-data-stats>-->
        <!--          </div>-->
      </div>
    </div>
  </section>
</template>

<style scoped></style>
